import * as React from 'react';

const CLOSE_THRESHOLD_PX = 72;
const MAX_DRAG_PX = 520;

type DragState = {
  pointerId: number;
  startY: number;
  offsetY: number;
  /** Element that receives the translate — one sheet only. */
  sheet: HTMLElement;
};

function getDrawerRoot(zone: HTMLElement): HTMLElement | null {
  return zone.closest(
    '.scope-pwa-drawer-root, .scope-pwa-bottom-sheet'
  ) as HTMLElement | null;
}

/**
 * Prefer the visible Ant content-wrapper. CSSMotion can leave a hidden twin in
 * the DOM; moving both (or fighting Ant motion on both) paints a duplicate sheet.
 */
function resolveSheetElement(zone: HTMLElement): HTMLElement | null {
  const root = getDrawerRoot(zone);
  if (!root) return null;

  if (root.classList.contains('scope-pwa-bottom-sheet')) {
    return root;
  }

  const wrappers = root.querySelectorAll<HTMLElement>(
    '.ant-drawer-content-wrapper'
  );
  for (let i = 0; i < wrappers.length; i += 1) {
    const el = wrappers[i];
    if (!el.classList.contains('ant-drawer-content-wrapper-hidden')) {
      return el;
    }
  }
  return wrappers[0] ?? root;
}

function setSheetOffset(
  sheet: HTMLElement,
  offsetPx: number,
  animate: boolean
) {
  sheet.style.setProperty(
    'transform',
    `translate3d(0, ${Math.max(0, offsetPx)}px, 0)`,
    'important'
  );
  sheet.style.setProperty(
    'transition',
    animate ? 'transform 220ms ease-out' : 'none',
    'important'
  );
}

function clearSheetOffset(sheet: HTMLElement) {
  sheet.style.removeProperty('transform');
  sheet.style.removeProperty('transition');
  // Legacy CSS-var path (custom sheets may still read this).
  sheet.style.removeProperty('--pwa-sheet-drag-offset');
  sheet.classList.remove('scope-pwa-drawer-root--dragging');
  sheet.classList.remove('scope-pwa-drawer-root--drag-closing');
  const root = sheet.closest('.scope-pwa-drawer-root, .scope-pwa-bottom-sheet');
  if (root && root !== sheet) {
    (root as HTMLElement).style.removeProperty('--pwa-sheet-drag-offset');
    root.classList.remove('scope-pwa-drawer-root--dragging');
    root.classList.remove('scope-pwa-drawer-root--drag-closing');
  }
}

/** Clear in-progress / stuck PWA sheet drag transforms (e.g. after a nested modal closes). */
export function resetPwaSheetDragState(): void {
  document
    .querySelectorAll(
      '.scope-pwa-drawer-root .ant-drawer-content-wrapper, .scope-pwa-bottom-sheet'
    )
    .forEach((node) => {
      clearSheetOffset(node as HTMLElement);
    });
  document
    .querySelectorAll('.ant-drawer-content-wrapper-hidden')
    .forEach((node) => {
      (node as HTMLElement).style.setProperty('display', 'none', 'important');
    });
}

const CONTENT_DRAG_START_PX = 12;

/**
 * Pull-down-to-close for PWA bottom-sheet drawers. Attach returned handlers to the
 * header / drag-zone element at the top of the sheet.
 *
 * `contentRef` is the scrolling body. When it is already at the top, a downward
 * pull dismisses the sheet instead of being eaten as a no-op scroll.
 */
export function usePwaBottomSheetDragClose(
  enabled: boolean,
  onClose: () => void,
  dragZoneRef: React.RefObject<HTMLElement | null>,
  contentRef?: React.RefObject<HTMLElement | null>
): Pick<
  React.HTMLAttributes<HTMLElement>,
  'onPointerDown' | 'onPointerMove' | 'onPointerUp' | 'onPointerCancel'
> & { style: React.CSSProperties | undefined } {
  const dragStateRef = React.useRef<DragState | null>(null);

  React.useEffect(() => {
    if (!enabled) return;
    const zone = dragZoneRef.current;
    if (!zone) return;
    const sheet = resolveSheetElement(zone);
    if (sheet) clearSheetOffset(sheet);
  }, [enabled, dragZoneRef]);

  const finishDrag = React.useCallback(
    (shouldClose: boolean) => {
      const state = dragStateRef.current;
      if (!state) return;

      dragStateRef.current = null;
      const { sheet } = state;

      if (shouldClose) {
        setSheetOffset(sheet, sheet.clientHeight || window.innerHeight, true);
        window.setTimeout(() => {
          clearSheetOffset(sheet);
          onClose();
        }, 220);
        return;
      }

      clearSheetOffset(sheet);
    },
    [onClose]
  );

  const onPointerDown = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      if (!enabled) return;
      const zone = dragZoneRef.current;
      if (!zone) return;

      const target = event.target as HTMLElement;
      if (target.closest('button, a, [role="button"], input, textarea, select')) {
        return;
      }

      const sheet = resolveSheetElement(zone);
      if (!sheet) return;

      // Hide CSSMotion leftovers so only one sheet can paint.
      sheet
        .closest('.ant-drawer')
        ?.querySelectorAll('.ant-drawer-content-wrapper-hidden')
        .forEach((node) => {
          (node as HTMLElement).style.setProperty('display', 'none', 'important');
        });

      dragStateRef.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        offsetY: 0,
        sheet,
      };
      setSheetOffset(sheet, 0, false);
      zone.setPointerCapture(event.pointerId);
      event.preventDefault();
    },
    [enabled, dragZoneRef]
  );

  const onPointerMove = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;

      const delta = event.clientY - state.startY;
      const offset = Math.max(0, Math.min(delta, MAX_DRAG_PX));
      state.offsetY = offset;
      setSheetOffset(state.sheet, offset, false);
      event.preventDefault();
    },
    []
  );

  const onPointerUp = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;

      const zone = dragZoneRef.current;
      if (zone?.hasPointerCapture(event.pointerId)) {
        zone.releasePointerCapture(event.pointerId);
      }

      finishDrag(state.offsetY >= CLOSE_THRESHOLD_PX);
      event.preventDefault();
    },
    [dragZoneRef, finishDrag]
  );

  const onPointerCancel = React.useCallback(
    (event: React.PointerEvent<HTMLElement>) => {
      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;

      const zone = dragZoneRef.current;
      if (zone?.hasPointerCapture(event.pointerId)) {
        zone.releasePointerCapture(event.pointerId);
      }

      dragStateRef.current = null;
      clearSheetOffset(state.sheet);
      event.preventDefault();
    },
    [dragZoneRef]
  );

  React.useEffect(() => {
    if (!enabled) return;
    const content = contentRef?.current;
    if (!content) return;

    let pointerId: number | null = null;
    let startY = 0;
    let tracking = false;
    let suppressClick = false;
    let scroller: HTMLElement = content;

    const onDown = (event: PointerEvent) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      const target = event.target as HTMLElement | null;
      if (target?.closest('input, textarea, select')) return;
      scroller = target ? scrollableWithin(target, content) : content;
      if (scroller.scrollTop > 0) return;
      pointerId = event.pointerId;
      startY = event.clientY;
      tracking = false;
    };

    const onMove = (event: PointerEvent) => {
      if (pointerId == null || event.pointerId !== pointerId) return;
      const delta = event.clientY - startY;
      if (!tracking) {
        if (delta < -CONTENT_DRAG_START_PX || scroller.scrollTop > 0) {
          pointerId = null;
          return;
        }
        if (delta < CONTENT_DRAG_START_PX) return;

        const zone = dragZoneRef.current;
        const sheet = zone ? resolveSheetElement(zone) : getSheetFromContent(content);
        if (!sheet) {
          pointerId = null;
          return;
        }
        tracking = true;
        dragStateRef.current = {
          pointerId: event.pointerId,
          startY,
          offsetY: 0,
          sheet,
        };
        setSheetOffset(sheet, 0, false);
        content.setPointerCapture(event.pointerId);
      }

      const state = dragStateRef.current;
      if (!state || event.pointerId !== state.pointerId) return;
      const offset = Math.max(0, Math.min(event.clientY - state.startY, MAX_DRAG_PX));
      state.offsetY = offset;
      setSheetOffset(state.sheet, offset, false);
      event.preventDefault();
    };

    const end = (event: PointerEvent) => {
      if (pointerId == null || event.pointerId !== pointerId) return;
      pointerId = null;
      if (!tracking) return;
      tracking = false;
      if (content.hasPointerCapture(event.pointerId)) {
        content.releasePointerCapture(event.pointerId);
      }
      const state = dragStateRef.current;
      if ((state?.offsetY ?? 0) > CONTENT_DRAG_START_PX) {
        suppressClick = true;
      }
      finishDrag((state?.offsetY ?? 0) >= CLOSE_THRESHOLD_PX);
    };

    const onClick = (event: MouseEvent) => {
      if (!suppressClick) return;
      suppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    const cancel = (event: PointerEvent) => {
      if (pointerId == null || event.pointerId !== pointerId) return;
      pointerId = null;
      tracking = false;
      const state = dragStateRef.current;
      dragStateRef.current = null;
      if (state) clearSheetOffset(state.sheet);
    };

    const onTouchMove = (event: TouchEvent) => {
      if (!tracking) return;
      event.preventDefault();
    };

    content.addEventListener('pointerdown', onDown);
    content.addEventListener('pointermove', onMove);
    content.addEventListener('pointerup', end);
    content.addEventListener('pointercancel', cancel);
    content.addEventListener('click', onClick, true);
    content.addEventListener('touchmove', onTouchMove, { passive: false });
    return () => {
      content.removeEventListener('pointerdown', onDown);
      content.removeEventListener('pointermove', onMove);
      content.removeEventListener('pointerup', end);
      content.removeEventListener('pointercancel', cancel);
      content.removeEventListener('click', onClick, true);
      content.removeEventListener('touchmove', onTouchMove);
    };
  }, [enabled, contentRef, dragZoneRef, finishDrag]);

  return {
    onPointerDown,
    onPointerMove,
    onPointerUp,
    onPointerCancel,
    style: enabled ? { touchAction: 'none' } : undefined,
  };
}

function getSheetFromContent(content: HTMLElement): HTMLElement | null {
  return content.closest(
    '.scope-pwa-bottom-sheet, .scope-pwa-drawer-root'
  ) as HTMLElement | null;
}

/** The element that actually scrolls under the pointer (list inside a sheet body). */
function scrollableWithin(target: HTMLElement, root: HTMLElement): HTMLElement {
  let node: HTMLElement | null = target;
  while (node && node !== root) {
    const overflowY = window.getComputedStyle(node).overflowY;
    if (
      (overflowY === 'auto' || overflowY === 'scroll') &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return root;
}
